import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout";
import { Button } from "@dashboard/components/Button";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import TableButtonWrapper from "@dashboard/components/TableButtonWrapper";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import TableRowLink from "@dashboard/components/TableRowLink";
import { configurationMenuUrl } from "@dashboard/configuration";
import { sectionNames } from "@dashboard/intl";
import { renderCollection, stopPropagation } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { DeleteIcon, IconButton, ResponsiveTable } from "@saleor/macaw-ui";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { ExchangeRateForm } from "./components";
import { mutations, queries } from "./index";
import { useStyles } from "./styles";
import { ExchangeRate } from "./types";
import { ExchangeRateListUrlQueryParams } from "./urls";
import { useIntl, FormattedMessage } from "react-intl";

interface ExchangeRateListProps {
  params: ExchangeRateListUrlQueryParams;
}

const numberOfColumns = 3;

const ExchangeRateList: React.FC<ExchangeRateListProps> = () => {
  const intl = useIntl();
  const classes = useStyles({});
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedExchangeRate, setSelectedExchangeRate] = useState<ExchangeRate | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exchangeRateToDelete, setExchangeRateToDelete] = useState<ExchangeRate | null>(null);
  const [, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Consulta para obtener la lista de tipos de cambio
  const { data, refetch } = useQuery(queries.exchangeRateList);

  // Mutación para crear un nuevo tipo de cambio
  const [createExchangeRate, { loading: createLoading }] = useMutation(
    mutations.createExchangeRate,
    {
      onCompleted: () => {
        setFormOpen(false);
        refetch();
        showNotification("Tipo de cambio creado correctamente", "success");
      },
      onError: error => {
        showNotification(`Error al crear: ${error.message}`, "error");
      },
    },
  );

  // Mutación para actualizar un tipo de cambio existente
  const [updateExchangeRate, { loading: updateLoading }] = useMutation(
    mutations.updateExchangeRate,
    {
      onCompleted: () => {
        setFormOpen(false);
        refetch();
        showNotification("Tipo de cambio actualizado correctamente", "success");
      },
      onError: error => {
        showNotification(`Error al actualizar: ${error.message}`, "error");
      },
    },
  );

  // Mutación para eliminar un tipo de cambio
  const [deleteExchangeRate] = useMutation(mutations.deleteExchangeRate, {
    onCompleted: () => {
      refetch();
      showNotification("Tipo de cambio eliminado correctamente", "success");
    },
    onError: error => {
      showNotification(`Error al eliminar: ${error.message}`, "error");
    },
  });

  const showNotification = (message: string, severity: "success" | "error") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCreateClick = () => {
    setFormMode("create");
    setSelectedExchangeRate(undefined);
    setFormOpen(true);
  };

  const handleEditClick = (exchangeRate: ExchangeRate) => {
    setFormMode("edit");
    setSelectedExchangeRate(exchangeRate);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
  };

  const handleFormSubmit = (data: ExchangeRate) => {
    if (formMode === "create") {
      createExchangeRate({
        variables: {
          currencyCode: data.currencyCode,
          exchangeRate: data.exchangeRate,
        },
      });
    } else {
      updateExchangeRate({
        variables: {
          id: selectedExchangeRate?.id,
          currencyCode: data.currencyCode,
          exchangeRate: data.exchangeRate,
        },
      });
    }
  };

  const exchangeRates: ExchangeRate[] = data?.exchangeRates || [];

  const onRemove = (exchangeRate: ExchangeRate) => {
    setExchangeRateToDelete(exchangeRate);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (exchangeRateToDelete) {
      deleteExchangeRate({
        variables: { id: exchangeRateToDelete.id },
      });
      setDeleteDialogOpen(false);
      setExchangeRateToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setExchangeRateToDelete(null);
  };

  return (
    <ListPageLayout>
      <TopNav href={configurationMenuUrl} title={intl.formatMessage(sectionNames.exchangeRates)}>
        <Button onClick={handleCreateClick} variant="primary" data-test-id="add-exchange-rate">
          <FormattedMessage
            id="TpiKcI"
            defaultMessage="Crear Tipo de Cambio"
            description="button"
          />
        </Button>
      </TopNav>

      <DashboardCard>
        <ResponsiveTable>
          <TableHead>
            <TableRowLink>
              <TableCellHeader>
                <FormattedMessage
                  id="zSlbpK"
                  defaultMessage="Currency Code"
                  description="currency code"
                />
              </TableCellHeader>
              <TableCellHeader className={classes.colExchangeRate}>
                <FormattedMessage
                  id="E7W5n9"
                  defaultMessage="Exchange Rate"
                  description="exchange rate"
                />
              </TableCellHeader>
              <TableCell className={classes.colRight}>
                <FormattedMessage
                  id="VHuzgq"
                  defaultMessage="Actions"
                  description="table actions"
                />
              </TableCell>
            </TableRowLink>
          </TableHead>
          <TableBody data-test-id="exchange-rate-list">
            {renderCollection(
              exchangeRates,
              exchangeRate => (
                <TableRowLink
                  data-test-id="exchange-rate-row"
                  hover={!!exchangeRate}
                  key={exchangeRate ? exchangeRate.id : "skeleton"}
                  className={classes.tableRow}
                >
                  <TableCell className={classes.colCurrencyCode}>
                    <span data-test-id="currency-code">
                      {exchangeRate?.currencyCode || <Skeleton />}
                    </span>
                  </TableCell>
                  <TableCell className={classes.colExchangeRate}>
                    <span data-test-id="exchange-rate">
                      {exchangeRate?.exchangeRate || <Skeleton />}
                    </span>
                  </TableCell>
                  <TableCell className={classes.colAction}>
                    <div style={{ display: "flex", gap: "5px", justifyContent: "flex-end" }}>
                      <TableButtonWrapper>
                        <IconButton
                          variant="secondary"
                          color="primary"
                          data-test-id="edit-exchange-rate"
                          onClick={
                            exchangeRate
                              ? stopPropagation(() => handleEditClick(exchangeRate))
                              : undefined
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </TableButtonWrapper>
                      <TableButtonWrapper>
                        <IconButton
                          variant="secondary"
                          color="primary"
                          data-test-id="delete-exchange-rate"
                          onClick={
                            exchangeRate ? stopPropagation(() => onRemove(exchangeRate)) : undefined
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableButtonWrapper>
                    </div>
                  </TableCell>
                </TableRowLink>
              ),
              () => (
                <TableRowLink>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage id="gGYer6" defaultMessage="No exchange rates found" />
                  </TableCell>
                </TableRowLink>
              ),
            )}
          </TableBody>
        </ResponsiveTable>
      </DashboardCard>

      <ExchangeRateForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={selectedExchangeRate}
        loading={createLoading || updateLoading}
        mode={formMode}
      />

      <ActionDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        variant="delete"
        title={intl.formatMessage({
          id: "delete-exchange-rate",
          defaultMessage: "Eliminar Tipo de Cambio",
          description: "dialog header",
        })}
      >
        <FormattedMessage
          id="delete-exchange-rate-confirmation"
          defaultMessage="¿Está seguro que desea eliminar el tipo de cambio {code}?"
          values={{
            code: <strong>{exchangeRateToDelete?.currencyCode}</strong>,
          }}
        />
      </ActionDialog>
    </ListPageLayout>
  );
};

export default ExchangeRateList;
